import { useState } from 'react'
import { Link } from 'react-router'
import { KeyRound, Plus } from 'lucide-react'
import { sshKeys as keyApi } from '@/api/endpoints'
import type { SshKey } from '@/api/types'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Ago } from '@/components/Ago'
import { CopyBox } from '@/components/CopyBox'
import { Err } from '@/components/Err'
import { Empty, PageHead, Section } from '@/components/layout/Page'
import { useMinute } from '@/hooks/useNow'
import { usePoll } from '@/hooks/usePoll'
import { cn } from '@/lib/utils'
import { exact } from '@/ui/format'
import { ACTION, ACTION_DANGER } from '@/views/vm/chips'

const KEYS_POLL_MS = 60000
const RECENT_MS = 7 * 24 * 60 * 60 * 1000

// SSH 키(#627) — GitHub 의 Settings › SSH and GPG keys 와 같은 모양이다. 계정 메뉴가 열고 세 역할이 같다.
// 제목 줄 오른쪽의 `새 SSH 키` 가 새 키 페이지로 가고, `인증 키` 목록의 한 줄이 키 하나다:
// 키 아이콘과 SSH 표시 · 제목 · 지문 · 추가한 날 · 마지막 사용 · 삭제. 최근 한 주 안에 쓴 키는 아이콘이 초록이다.
// VM 이 만든 키(`lease_id`)는 `VM 키` 로 가린다. 개인키는 여기 오지 않는다.
// 자기 키가 하나라도 있으면 목록 아래에 백엔드가 만든 config 블록(`ssh_config`)을 둔다 — 게이트웨이의 주소와 포트뿐이라
// 어느 버전의 ssh 로든 `ssh <VM 이름>@<게이트웨이>` 로 닿고 키를 받을 일이 없다(#628).
export default function SshKeys() {
  const keys = usePoll(() => keyApi.list(), KEYS_POLL_MS)
  const rows = keys.data?.items ?? []
  const config = keys.data?.ssh_config ?? null
  const gateway = keys.data?.gateway ?? null
  const hasOwn = rows.some((k) => !k.lease_id)
  return (
    <>
      <PageHead
        icon={<KeyRound className="size-5" />}
        title="SSH 키"
        right={
          <Button asChild className="bg-brand hover:bg-brand-hover">
            <Link to="/me/keys/new">
              <Plus className="size-3.5" />새 SSH 키
            </Link>
          </Button>
        }
      />
      <p className="text-[12px] text-muted-foreground">알아보지 못하는 키는 삭제하세요.</p>

      <Section title="인증 키">
        <Err error={keys.error} />
        {rows.length === 0 ? (
          <Empty>{keys.loading ? '불러오는 중' : '등록된 SSH 키가 없습니다.'}</Empty>
        ) : (
          <div className="divide-y rounded-lg border bg-card">
            {rows.map((k) => (
              <KeyRow key={k.id} k={k} onDeleted={keys.refresh} />
            ))}
          </div>
        )}
      </Section>

      {config && hasOwn ? (
        <Section title="~/.ssh/config">
          <div className="grid gap-1.5">
            <CopyBox text={config} label="ssh config 복사" />
            {gateway ? (
              <p className="text-[11px] text-muted-foreground">
                붙인 뒤 <span className="font-mono">ssh &lt;VM 이름&gt;@{gateway.host}</span> 로 접속하세요.
              </p>
            ) : null}
          </div>
        </Section>
      ) : null}
    </>
  )
}

// 최근 한 주 안에 쓴 키인가 — GitHub 처럼 아이콘의 색이 말한다. 지금 시각은 시계에서 받는다 — 같은 목록은 다시 그려지지 않아
// 렌더의 지금으로는 한 주가 지나도 색이 그대로다.
function usedRecently(iso: string | null, now: number): boolean {
  return iso !== null && now - Date.parse(iso) < RECENT_MS
}

function KeyRow({ k, onDeleted }: { k: SshKey; onDeleted: () => void }) {
  const now = useMinute()
  const recent = usedRecently(k.last_used_at, now)
  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <div className={cn('flex w-10 shrink-0 flex-col items-center gap-1', recent ? 'text-ok-ink' : 'text-muted-foreground')}>
        <KeyRound className="size-6" aria-hidden />
        <span className="rounded border border-current px-1 text-[9px] leading-tight font-semibold">SSH</span>
      </div>
      <div className="grid min-w-0 flex-1 gap-0.5 text-[12px]">
        <span className="truncate text-[13px] font-semibold">
          {k.name}
          {k.lease_id ? <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">VM 키</span> : null}
        </span>
        <span className="truncate font-mono text-[11px] text-muted-foreground">{k.fingerprint}</span>
        <span className="text-muted-foreground">추가 {exact(k.created_at).slice(0, 10)}</span>
        <span className="text-muted-foreground">{k.last_used_at ? <>마지막 사용 <Ago at={k.last_used_at} /></> : '사용한 적 없음'}</span>
      </div>
      <DeleteButton keyRow={k} onDeleted={onDeleted} />
    </div>
  )
}

// 삭제는 되돌릴 수 없고 도는 VM 에서도 빠진다 — 제목 하나, 결과 한 문장, 버튼 둘의 대화 상자다.
// 쓰는 중인 VM 의 키는 백엔드가 409(key_in_use)로 거절하고 그 문장이 창에 선다.
function DeleteButton({ keyRow: k, onDeleted }: { keyRow: SshKey; onDeleted: () => void }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const remove = async () => {
    setBusy(true)
    setError(null)
    try {
      await keyApi.remove(k.id)
      setOpen(false)
      onDeleted()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <button type="button" className={`${ACTION} ${ACTION_DANGER}`} onClick={() => (setError(null), setOpen(true))} aria-label={`${k.name} 삭제`}>
        삭제
      </button>
      <Dialog open={open} onOpenChange={(v) => (v || busy ? undefined : setOpen(false))}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{k.name} 삭제</DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-muted-foreground">이 키로는 더 이상 접속할 수 없습니다.</p>
          <Err error={error} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              취소
            </Button>
            <Button type="button" onClick={remove} disabled={busy} className="bg-danger text-white hover:bg-danger/90">
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
